//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  IContentLibraryItem,
  IDeserializedContentLibraryItem,
  IMediaAdMetadata,
} from '@apis/content-library/contentLibraryApiModel';
import { isDefined } from '@utils/filterPredicates';
import { Serialized } from '@utils/types/Serialized';

export function isGif(item: IContentLibraryItem): boolean {
  const contentType = item.metadata.contentType;
  if (contentType) {
    return contentType === 'image/gif';
  }

  const numFrames = item.metadata.numFrames ?? 1;
  const isVideoItem = isVideo(item); // Ignore videos with GIF thumbnails

  return !isVideoItem && numFrames > 1;
}

export function isVideo(item: IContentLibraryItem): boolean {
  return !!item.metadata.videoSource;
}

export function deserializeAdMetadata(
  metadata?: Serialized<IMediaAdMetadata>,
): IMediaAdMetadata {
  return {
    ...metadata,
    adCode: isDefined(metadata?.adCode)
      ? {
          ...metadata.adCode,
          createdAt: new Date(metadata.adCode.createdAt),
          expirationTime: metadata.adCode.expirationTime
            ? new Date(metadata.adCode.expirationTime)
            : undefined,
        }
      : undefined,
  };
}

// Use computedImpressions first, as it's the canonical field for sorting.
// This ensures consistency across views since impressions can often be missing from metadata.
// If computedImpressions is unavailable, fall back to videoViews for video content,
// and finally use impressions as a last resort.
export function getImpressionsValue(
  item: IDeserializedContentLibraryItem | IContentLibraryItem,
) {
  return (
    item.socialDetails?.stats?.computedImpressions ??
    item.socialDetails?.stats?.videoViews ??
    item.socialDetails?.stats?.impressions
  );
}
